// ColorPreviewCanvas.js - Optimized with caching and precomputed alpha map
import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import { toProxiedUrl } from '../../utils/imageProxyUtils';

const MAX_IMAGE_WIDTH = 3000;
const BACKGROUND_TOLERANCE = 60;
const TRANSITION_ZONE = 3;
const TRANSPARENCY_DETECTION_THRESHOLD = 0.05;

// Color name → hex map (unchanged)
const COLOR_NAME_MAP = {
  red: '#FF0000', blue: '#0000FF', green: '#00FF00', yellow: '#FFFF00',
  black: '#000000', white: '#FFFFFF', purple: '#800080', orange: '#FFA500',
  pink: '#FFC0CB', brown: '#A52A2A', gray: '#808080', grey: '#808080',
  cyan: '#00FFFF', magenta: '#FF00FF', lime: '#00FF00', maroon: '#800000',
  navy: '#000080', olive: '#808000', teal: '#008080', aqua: '#00FFFF',
  fuchsia: '#FF00FF', silver: '#C0C0C0', gold: '#FFD700', indigo: '#4B0082',
  violet: '#EE82EE', coral: '#FF7F50', crimson: '#DC143C', darkblue: '#00008B',
  darkcyan: '#008B8B', darkgoldenrod: '#B8860B', darkgray: '#A9A9A9',
  darkgreen: '#006400', darkkhaki: '#BDB76B', darkmagenta: '#8B008B',
  darkolivegreen: '#556B2F', darkorange: '#FF8C00', darkorchid: '#9932CC',
  darkred: '#8B0000', darksalmon: '#E9967A', darkseagreen: '#8FBC8F',
  darkslateblue: '#483D8B', darkslategray: '#2F4F4F', darkturquoise: '#00CED1',
  darkviolet: '#9400D3', deeppink: '#FF1493', deepskyblue: '#00BFFF',
  dimgray: '#696969', dodgerblue: '#1E90FF', firebrick: '#B22222',
  floralwhite: '#FFFAF0', forestgreen: '#228B22', gainsboro: '#DCDCDC',
  ghostwhite: '#F8F8FF', greenyellow: '#ADFF2F', honeydew: '#F0FFF0',
  hotpink: '#FF69B4', indianred: '#CD5C5C', ivory: '#FFFFF0', khaki: '#F0E68C',
  lavender: '#E6E6FA', lavenderblush: '#FFF0F5', lawngreen: '#7CFC00',
  lemonchiffon: '#FFFACD', lightblue: '#ADD8E6', lightcoral: '#F08080',
  lightcyan: '#E0FFFF', lightgoldenrodyellow: '#FAFAD2', lightgray: '#D3D3D3',
  lightgreen: '#90EE90', lightpink: '#FFB6C1', lightsalmon: '#FFA07A',
  lightseagreen: '#20B2AA', lightskyblue: '#87CEFA', lightslategray: '#778899',
  lightsteelblue: '#B0C4DE', lightyellow: '#FFFFE0', limegreen: '#32CD32',
  linen: '#FAF0E6', mediumaquamarine: '#66CDAA', mediumblue: '#0000CD',
  mediumorchid: '#BA55D3', mediumpurple: '#9370DB', mediumseagreen: '#3CB371',
  mediumslateblue: '#7B68EE', mediumspringgreen: '#00FA9A', mediumturquoise: '#48D1CC',
  mediumvioletred: '#C71585', midnightblue: '#191970', mintcream: '#F5FFFA',
  mistyrose: '#FFE4E1', moccasin: '#FFE4B5', navajowhite: '#FFDEAD',
  oldlace: '#FDF5E6', olivedrab: '#6B8E23', orangered: '#FF4500', orchid: '#DA70D6',
  palegoldenrod: '#EEE8AA', palegreen: '#98FB98', paleturquoise: '#AFEEEE',
  palevioletred: '#DB7093', papayawhip: '#FFEFD5', peachpuff: '#FFDAB9',
  peru: '#CD853F', plum: '#DDA0DD', powderblue: '#B0E0E6', rosybrown: '#BC8F8F',
  royalblue: '#4169E1', saddlebrown: '#8B4513', salmon: '#FA8072',
  sandybrown: '#F4A460', seagreen: '#2E8B57', seashell: '#FFF5EE',
  sienna: '#A0522D', skyblue: '#87CEEB', slateblue: '#6A5ACD',
  slategray: '#708090', snow: '#FFFAFA', springgreen: '#00FF7F',
  steelblue: '#4682B4', tan: '#D2B48C', thistle: '#D8BFD8', tomato: '#FF6347',
  turquoise: '#40E0D0', wheat: '#F5DEB3', whitesmoke: '#F5F5F5',
  yellowgreen: '#9ACD32',
  rouge: '#FF0000', bleu: '#0000FF', vert: '#00FF00', jaune: '#FFFF00',
  noir: '#000000', blanc: '#FFFFFF', rose: '#FFC0CB', marron: '#A52A2A',
  gris: '#808080', bordeaux: '#800000', marine: '#000080', argent: '#C0C0C0',
  or: '#FFD700', corail: '#FF7F50', cramoisi: '#DC143C',
  'bleu marine': '#000080', 'bleu ciel': '#87CEEB', 'vert clair': '#90EE90',
  'vert foncé': '#006400', 'rouge foncé': '#8B0000', 'rose clair': '#FFB6C1',
  'rose foncé': '#FF69B4', 'gris clair': '#D3D3D3', 'gris foncé': '#A9A9A9',
  beige: '#F5F5DC', crème: '#FFFDD0', lavande: '#E6E6FA', menthe: '#98FB98',
  pêche: '#FFDAB9', saumon: '#FA8072', terracotta: '#E2725B',
};

// ─── Helper functions ─────────────────────────────────────────────────────────
const parseColor = (color) => {
  if (!color) return [200, 200, 200];
  const lower = color.toLowerCase().trim();
  if (COLOR_NAME_MAP[lower]) color = COLOR_NAME_MAP[lower];

  const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (rgbMatch) return [+rgbMatch[1], +rgbMatch[2], +rgbMatch[3]];

  const rgbaMatch = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgbaMatch) return [+rgbaMatch[1], +rgbaMatch[2], +rgbaMatch[3]];

  if (color.startsWith('#')) {
    let hex = color.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
    ];
  }

  try {
    const div = document.createElement('div');
    div.style.color = color;
    div.style.display = 'none';
    document.body.appendChild(div);
    const computed = window.getComputedStyle(div).color;
    document.body.removeChild(div);
    const m = computed.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (m) return [+m[1], +m[2], +m[3]];
  } catch (_) {}

  return [200, 200, 200];
};

const colorDist = (r1, g1, b1, r2, g2, b2) =>
  Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2);

const hasExistingTransparency = (pixels, width, height) => {
  const { data } = pixels;
  let transparent = 0;
  let total = 0;

  for (let x = 0; x < width; x++) {
    if (data[x * 4 + 3] < 10) transparent++;
    if (data[((height - 1) * width + x) * 4 + 3] < 10) transparent++;
    total += 2;
  }
  for (let y = 0; y < height; y++) {
    if (data[(y * width) * 4 + 3] < 10) transparent++;
    if (data[(y * width + width - 1) * 4 + 3] < 10) transparent++;
    total += 2;
  }

  return transparent / total >= TRANSPARENCY_DETECTION_THRESHOLD;
};

const getBackgroundAlphaMap = (pixels, width, height) => {
  const { data } = pixels;
  const bgR = data[0], bgG = data[1], bgB = data[2];
  const alphaMap = new Float32Array(width * height).fill(255);
  const visited = new Uint8Array(width * height);
  const queue = [];

  const isBackground = (idx) =>
    colorDist(data[idx * 4], data[idx * 4 + 1], data[idx * 4 + 2], bgR, bgG, bgB) <= BACKGROUND_TOLERANCE;

  for (let x = 0; x < width; x++) {
    [x, (height - 1) * width + x].forEach(idx => {
      if (!visited[idx] && isBackground(idx)) {
        visited[idx] = 1; alphaMap[idx] = 0; queue.push(idx);
      }
    });
  }
  for (let y = 0; y < height; y++) {
    [y * width, y * width + (width - 1)].forEach(idx => {
      if (!visited[idx] && isBackground(idx)) {
        visited[idx] = 1; alphaMap[idx] = 0; queue.push(idx);
      }
    });
  }

  while (queue.length > 0) {
    const idx = queue.pop();
    const x = idx % width;
    const y = Math.floor(idx / width);
    const neighbors = [
      y > 0           ? idx - width : -1,
      y < height - 1  ? idx + width : -1,
      x > 0           ? idx - 1     : -1,
      x < width - 1   ? idx + 1     : -1,
    ];
    for (const nIdx of neighbors) {
      if (nIdx < 0 || nIdx >= width * height || visited[nIdx]) continue;
      if (isBackground(nIdx)) {
        visited[nIdx] = 1; alphaMap[nIdx] = 0; queue.push(nIdx);
      }
    }
  }

  const smoothAlpha = new Float32Array(alphaMap);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      if (alphaMap[idx] > 0) continue;
      for (let dy = -TRANSITION_ZONE; dy <= TRANSITION_ZONE; dy++) {
        for (let dx = -TRANSITION_ZONE; dx <= TRANSITION_ZONE; dx++) {
          const nx = x + dx, ny = y + dy;
          if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
          const nIdx = ny * width + nx;
          if (alphaMap[nIdx] === 0) continue;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist <= TRANSITION_ZONE) {
            const fade = (dist / TRANSITION_ZONE) ** 2 * 255;
            if (fade < smoothAlpha[nIdx]) smoothAlpha[nIdx] = fade;
          }
        }
      }
    }
  }

  return smoothAlpha;
};

// ─── Apply color using precomputed alpha map (if any) ─────────────────────────
const applyColorToCanvas = (canvas, originalPixels, color, precomputedAlphaMap = null) => {
  if (!canvas || !originalPixels) return;

  const ctx = canvas.getContext('2d');
  const imageData = ctx.createImageData(originalPixels);
  const { data } = imageData;
  const orig = originalPixels.data;
  const width = canvas.width;
  const height = canvas.height;

  const [targetR, targetG, targetB] = parseColor(color);

  const alreadyTransparent = hasExistingTransparency(originalPixels, width, height);
  const alphaMap = precomputedAlphaMap ?? (alreadyTransparent ? null : getBackgroundAlphaMap(originalPixels, width, height));

  for (let i = 0; i < orig.length; i += 4) {
    const pixelIdx = i / 4;
    const or = orig[i], og = orig[i + 1], ob = orig[i + 2], oa = orig[i + 3];

    const finalAlpha = alreadyTransparent
      ? oa
      : (alphaMap[pixelIdx] < 1 ? 0 : Math.round((oa / 255) * alphaMap[pixelIdx]));

    if (finalAlpha < 1) {
      data[i] = data[i + 1] = data[i + 2] = data[i + 3] = 0;
      continue;
    }

    const brightness = (or + og + ob) / 3;

    if (brightness < 60) {
      const tintFactor = brightness / 255;
      data[i]     = Math.min(255, Math.round(targetR * tintFactor * 0.5));
      data[i + 1] = Math.min(255, Math.round(targetG * tintFactor * 0.5));
      data[i + 2] = Math.min(255, Math.round(targetB * tintFactor * 0.5));
      data[i + 3] = finalAlpha;
    } else {
      const bf = brightness / 255;
      data[i]     = Math.min(255, Math.max(0, Math.round(targetR * bf)));
      data[i + 1] = Math.min(255, Math.max(0, Math.round(targetG * bf)));
      data[i + 2] = Math.min(255, Math.max(0, Math.round(targetB * bf)));
      data[i + 3] = finalAlpha;
    }
  }

  ctx.putImageData(imageData, 0, 0);
};

// ─── Global cache for generated dataURLs (key = imageUrl|color) ───────────────
const canvasCache = new Map(); // key: `${imageUrl}|${color}`, value: dataURL

// ─── Component ────────────────────────────────────────────────────────────────
const ColorPreviewCanvas = forwardRef(({
  imageUrl,
  color,
  className = '',
  style = {},
  onLoad = () => {},
  onError = () => {},
}, ref) => {
  const canvasRef           = useRef(null);
  const originalPixelsRef   = useRef(null);
  const precomputedAlphaRef = useRef(null);
  const currentColorRef     = useRef(color);
  const animationFrameRef   = useRef(null);
  const isApplyingRef       = useRef(false);
  const abortRef            = useRef(false);

  const [isLoaded, setIsLoaded]     = useState(false);
  const [imageError, setImageError] = useState(false);

  // Schedule color application with requestAnimationFrame
  const scheduleColorApply = useCallback((canvas, pixels, col, alphaMap) => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = requestAnimationFrame(() => {
      applyColorToCanvas(canvas, pixels, col, alphaMap);
      animationFrameRef.current = null;
      isApplyingRef.current = false;
    });
  }, []);

  // Load image and optionally compute alpha map once
  const loadImage = useCallback((url) => {
    if (!url) return;
    abortRef.current = false;
    const sourceUrl = toProxiedUrl(url);

    const img = new Image();
    img.crossOrigin = 'anonymous';

    const TIMEOUT_MS = 15000;
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      if (!abortRef.current) {
        console.error('ColorPreviewCanvas: timeout loading', sourceUrl);
        setImageError(true);
        onError(new Error('Image load timeout'));
      }
    }, TIMEOUT_MS);

    img.onload = () => {
      clearTimeout(timeoutId);
      if (abortRef.current || timedOut) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      let targetWidth  = img.width;
      let targetHeight = img.height;
      if (img.width > MAX_IMAGE_WIDTH) {
        targetWidth  = MAX_IMAGE_WIDTH;
        targetHeight = Math.round((MAX_IMAGE_WIDTH * img.height) / img.width);
      }

      canvas.width  = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled  = true;
      ctx.imageSmoothingQuality  = 'high';
      ctx.clearRect(0, 0, targetWidth, targetHeight);
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      try {
        const pixels = ctx.getImageData(0, 0, targetWidth, targetHeight);
        originalPixelsRef.current = pixels;

        // Precompute alpha map once (reused for all colors on this image)
        const alreadyTransparent = hasExistingTransparency(pixels, targetWidth, targetHeight);
        if (!alreadyTransparent) {
          precomputedAlphaRef.current = getBackgroundAlphaMap(pixels, targetWidth, targetHeight);
        } else {
          precomputedAlphaRef.current = null;
        }

        setIsLoaded(true);
        onLoad();

        // Check cache for current color before applying
        if (currentColorRef.current) {
          const cacheKey = `${url}|${currentColorRef.current}`;
          const cachedDataURL = canvasCache.get(cacheKey);
          if (cachedDataURL) {
            const cachedImg = new Image();
            cachedImg.onload = () => {
              ctx.clearRect(0, 0, targetWidth, targetHeight);
              ctx.drawImage(cachedImg, 0, 0, targetWidth, targetHeight);
            };
            cachedImg.src = cachedDataURL;
          } else {
            scheduleColorApply(canvas, pixels, currentColorRef.current, precomputedAlphaRef.current);
          }
        }
      } catch (err) {
        console.error('ColorPreviewCanvas: canvas read error (CORS):', err);
        setImageError(true);
        onError(err);
      }
    };

    img.onerror = (err) => {
      clearTimeout(timeoutId);
      if (abortRef.current || timedOut) return;
      console.error('ColorPreviewCanvas: failed to load image:', sourceUrl, err);
      setImageError(true);
      onError(new Error(`Failed to load image: ${sourceUrl}`));
    };

    img.src = sourceUrl;
  }, [onLoad, onError, scheduleColorApply]);

  // Public API
  useImperativeHandle(ref, () => ({
    applyColor: (newColor) => {
      if (isApplyingRef.current) return;
      if (!canvasRef.current || !originalPixelsRef.current || !newColor) return;

      // Skip if same color as last applied
      if (currentColorRef.current === newColor) return;
      currentColorRef.current = newColor;

      const cacheKey = `${imageUrl}|${newColor}`;
      const cached = canvasCache.get(cacheKey);
      if (cached) {
        const ctx = canvasRef.current.getContext('2d');
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          ctx.drawImage(img, 0, 0);
        };
        img.src = cached;
        return;
      }

      isApplyingRef.current = true;
      scheduleColorApply(canvasRef.current, originalPixelsRef.current, newColor, precomputedAlphaRef.current);

      // After the scheduled apply, store the result in cache
      setTimeout(() => {
        if (canvasRef.current && originalPixelsRef.current) {
          const dataURL = canvasRef.current.toDataURL();
          canvasCache.set(cacheKey, dataURL);
          // Limit cache size (simple FIFO)
          if (canvasCache.size > 200) {
            const firstKey = canvasCache.keys().next().value;
            canvasCache.delete(firstKey);
          }
        }
      }, 50);
    },
    reloadImage: () => {
      setImageError(false);
      setIsLoaded(false);
      originalPixelsRef.current = null;
      precomputedAlphaRef.current = null;
      loadImage(imageUrl);
    },
    canvasRef,
    getDataURL: () => canvasRef.current?.toDataURL() ?? null,
  }), [imageUrl, loadImage, scheduleColorApply]);

  // Load image when URL changes
  useEffect(() => {
    abortRef.current = true;
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    setIsLoaded(false);
    setImageError(false);
    originalPixelsRef.current = null;
    precomputedAlphaRef.current = null;
    if (imageUrl) loadImage(imageUrl);
    return () => {
      abortRef.current = true;
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [imageUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reapply color when color prop changes (if already loaded)
  useEffect(() => {
    currentColorRef.current = color;
    if (isLoaded && color && canvasRef.current && originalPixelsRef.current) {
      const cacheKey = `${imageUrl}|${color}`;
      const cached = canvasCache.get(cacheKey);
      if (cached) {
        const ctx = canvasRef.current.getContext('2d');
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          ctx.drawImage(img, 0, 0);
        };
        img.src = cached;
      } else {
        scheduleColorApply(canvasRef.current, originalPixelsRef.current, color, precomputedAlphaRef.current);
      }
    }
  }, [color, isLoaded, imageUrl, scheduleColorApply]);

  // Cleanup
  useEffect(() => {
    return () => {
      abortRef.current = true;
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  if (!imageUrl || imageError) {
    return (
      <div
        className={`image-fallback ${className}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f3f4f6',
          borderRadius: 8,
          color: '#9ca3af',
          fontSize: 13,
          ...style,
        }}
      >
        <span>{imageError ? 'Image unavailable' : 'No image'}</span>
      </div>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        display: 'block',
        opacity: isLoaded ? 1 : 0,
        transition: 'opacity 0.2s ease',
        ...style,
      }}
    />
  );
});

ColorPreviewCanvas.displayName = 'ColorPreviewCanvas';

export default ColorPreviewCanvas;
